import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Flex,
  Image,
  Input,
  Upload,
  UploadProps,
  Typography,
  MenuProps,
  Dropdown,
  Popover,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  FileOutlined,
  PlusOutlined,
  SmileOutlined,
} from "@ant-design/icons";

import { NimblePicker as Picker } from "emoji-mart";
import data from "emoji-mart/data/all.json";
import "emoji-mart/css/emoji-mart.css";
import { TbPhotoSquareRounded } from "react-icons/tb";
import { BsPaperclip } from "react-icons/bs";
import { LiaPollHSolid } from "react-icons/lia";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import AudioPlayer from "../../common/AudioPlayer";
import { RootState } from "../../../redux/store";
import {
  addNewMessage,
  moveChatToTop,
  removeLastMsg,
  removeMessageById,
  replaceMessageById,
  updateMessageUpload,
  setCurrentChat,
  setMsgToForward,
  updateConversationList,
  updateCurrentChat,
} from "../../../redux/slices/chatSlice";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import {
  formatMessage,
  formatSingleChat,
  formatTimestamp,
} from "../../../utils";
import { useThemeContext } from "../../../context/ThemeContext";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import apiInstance from "../../../services/apiService";
import ENDPOINTS from "../../../services/endpoint";
import apiServicesmedia from "../../../services/apiServicesmedia";
import { compressImage } from "../../../services/mediaCompressor";
import CustomLoader from "../../common/UI/Loader";
import DateTimePicker from "../../common/DateTimePicker";
import PollCreator from "../../common/PollCreator";
import CustomAudioRecorder from "../../common/CustomAudioRecorder";
import "./index.scss";
import { storageService } from "../../../services/storageService";
import { STORAGE_KEYS, UPLOAD } from "../../../constants";
import uploadManager from "../../../services/uploadManager";
import audioCacheDB from "../../../services/audioCacheDB";
import messageCacheDB from "../../../services/messageCacheDB";
import { messageGraph } from "../../../services/messageGraph";
import { FEATURE_FLAGS } from "../../../constants";

import { useSendMessageMutation } from "../../../redux/services/chatsApi";

const { TextArea } = Input;
const { Text } = Typography;
const ClipIcon = BsPaperclip as unknown as React.FC;
const PhotosIcon = TbPhotoSquareRounded as unknown as React.FC;
const PollIcon = LiaPollHSolid as unknown as React.FC;

interface ChatFooterProps {
  setReplyingTo: any;
  scrollToBottom: (immediate?: boolean) => void;
  replyingTo: any;
  getChat: any;
}

const ChatFooter: React.FC<ChatFooterProps> = ({
  setReplyingTo,
  scrollToBottom,
  replyingTo,
  getChat,
}) => {
  const dispatch = useAppDispatch();
  const { currentChat, msgToForward, messagesList } = useSelector(
    (state: RootState) => state.chat,
  );
  const { theme } = useThemeContext();
  const { setCurrentScreen } = useChatSidebarContext();
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [inputVisible, setInputVisible] = useState<boolean>(true);
  const [voiceVisible, setVoiceVisible] = useState<boolean>(true);
  const [uploadVisible, setUploadVisible] = useState<boolean>(true);
  const [mediaType, setMediaType] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);
  const isSendingRef = useRef(false); // synchronous guard — resets as soon as input clears
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const textAreaRef = useRef<any>(null);
  const userId = storageService.getUserId();
  const [canSendMessage, setCanSendMessage] = useState<boolean>(true);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isNotGroupMember, setIsNotGroupMember] = useState<boolean>(false);
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [dateTimePickerOpen, setDateTimePickerOpen] = useState<boolean>(false);
  const [schedulerVisible, setSchedulerVisible] = useState<boolean>(true);
  const [pollCreatorModalOpen, setPollCreatorModalOpen] =
    useState<boolean>(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState<boolean>(false);
  const [fileDetails, setFileDetails] = useState<{
    name: string;
    size: string;
    type: string;
  } | null>(null);

  const [triggerSendMessage] = useSendMessageMutation();

  const deleteAttachment = () => {
    setAudioURL(null);
    setRecording(false);
    setInputVisible(true);
    setUploadVisible(true);
    setSchedulerVisible(true);
    setSelectedFile(null);
    setMediaType(null);
    setVoiceVisible(true);
    setPreviewUrl(null);
    setSelectedFile(null);
    setFileDetails(null);
  };

  const onEmojiSelect = (emoji: any) => {
    const input = textAreaRef.current?.resizableTextArea?.textArea;
    if (input) {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const text = message;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newMessage = before + emoji.native + after;
      setMessage(newMessage);
      setTimeout(() => {
        input.selectionStart = input.selectionEnd = start + emoji.native.length;
        input.focus();
      }, 0);
    } else {
      setMessage(message + emoji.native);
    }
    setEmojiPickerOpen(false);
  };

  const sendMessage = async (msg: any) => {
    const {
      content,
      phoneNumber,
      iseFone,
      messageId,
      replyMsg,
      chatId,
      isGroup,
      isBroadCast,
      replyMediaType,
      replyFilePath,
      date,
    } = msg;

    const senderId = storageService.getUserId();
    const senderName = storageService.getFullName() || "Me";

    const tempMessage = {
      isTempMsg: true,
      id: Date.now(),
      message: content,
      timestamp: formatTimestamp(new Date()),
      isUser: true,
      replyMessage: replyingTo ? replyMsg : null,
      replyToId: messageId,
      replyMediaType,
      replyFilePath,
      number: phoneNumber,
      senderId,
      senderName,
      mediaType: 0,
      createdAt: new Date().toISOString(),
    };
    scrollToBottom(true);
    dispatch(addNewMessage(tempMessage));
    setMessage("");
    isSendingRef.current = false;
    setReplyingTo(null);

    const body: any = { content, messageId, date };

    if (currentChat?.isNewChat) {
      body.sourceNumber = currentChat?.number;
      body.phoneNumber = phoneNumber;
    } else {
      body.chatId = chatId;
    }

    if (iseFone) body.messageId = messageId;

    if (isGroup && chatId) {
      body.chatId = chatId;
      body.isGroup = true;
    }

    if (isBroadCast && chatId) {
      body.chatId = chatId;
      body.isBroadCast = true;
    }

    try {
      const response = await triggerSendMessage(body).unwrap();
      const newMsg = formatMessage(
        response?.newMessage || response?.conversation,
        userId,
      );

      if (currentChat?.isNewChat) {
        setCurrentScreen("chats");
        const updatedConvo = formatSingleChat(response.conversation);
        dispatch(
          updateCurrentChat({
            id: updatedConvo.id,
            date: updatedConvo.date,
            time: updatedConvo.time,
            message: updatedConvo.message,
            isNewChat: false,
          }),
        );
        dispatch(updateConversationList(updatedConvo));
      } else {
        dispatch(
          updateConversationList({
            id: currentChat?.id,
            message: newMsg.message,
            time: newMsg.time,
            date: newMsg.date,
            lastUpdateToSort: newMsg.createdAt,
            ofUser: userId,
          }),
        );
      }

      if (FEATURE_FLAGS.USE_CRDT_ENGINE && currentChat?.id && newMsg.id) {
        messageGraph.mirror(currentChat.id, newMsg);
      }
      dispatch(addNewMessage(newMsg));

      // Cache sent message in IndexedDB
      if (userId && currentChat?.id && newMsg.id) {
        messageCacheDB
          .putMessages(userId, currentChat.id, [newMsg])
          .catch(() => {});
      }

      dispatch(
        updateCurrentChat({
          message: newMsg.message,
        }),
      );

      dispatch(moveChatToTop({ id: currentChat?.id || "" }));
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error(
        error?.data?.message || error.message || "Failed to send message",
      );
    } finally {
      setSendingMessage(false);
      setReplyingTo(null);
    }
  };

  const sendMediaMessage = async (task: any) => {
    if (!currentChat?.id) return;

    setSelectedFile(null);
    setMediaType(null);
    setAudioURL(null);
    setRecording(false);
    setInputVisible(true);
    setVoiceVisible(true);
    setUploadVisible(true);
    setSchedulerVisible(true);
    setReplyingTo(null);
    setPreviewUrl(null);

    const {
      content,
      mediaType,
      mediaPath,
      file,
      isGroup,
      isBroadCast,
      replyTo,
      date,
    } = task;

    // Compress image before upload (mediaType 1 = image)
    let fileToUpload = file;
    if (file && mediaType === 1) {
      fileToUpload = await compressImage(file, {
        maxDimension: UPLOAD.COMPRESS_MAX_DIMENSION,
        quality: UPLOAD.COMPRESS_QUALITY,
        skipUnder: UPLOAD.COMPRESS_SKIP_UNDER,
      });
    }

    const formData = new FormData();
    formData.append("content", content);
    formData.append("mediaType", mediaType);
    if (date) {
      formData.append("date", date);
    }

    if (mediaPath) {
      formData.append("mediaPath", mediaPath);
    } else if (fileToUpload) {
      formData.append("file", fileToUpload);
    }

    if (replyTo) {
      formData.append("messageId", replyTo?.id);
    }

    formData.append("chatId", currentChat.id);

    if (isGroup) {
      formData.append("isGroup", isGroup);
    } else if (isBroadCast) {
      formData.append("isBroadCast", isBroadCast);
    }
    const senderId = storageService.getUserId();
    const senderName = storageService.getFullName() || "Me";

    const mediaTempId = Date.now();
    const taskId = String(mediaTempId);

    // Build a local preview URL so the bubble shows a thumbnail while uploading.
    let localPreview: string | undefined;
    if (fileToUpload && mediaType === 1) {
      localPreview = URL.createObjectURL(file); // original uncompressed for preview
    } else if (fileToUpload && mediaType === 2) {
      localPreview = URL.createObjectURL(fileToUpload); // video thumbnail preview
    } else if (fileToUpload && mediaType === 4) {
      // Cache audio blob in IndexedDB for persistence across chat switches / refresh
      try {
        await audioCacheDB.put(
          taskId,
          fileToUpload,
          currentChat.id,
          "uploading",
        );
      } catch (e) {
        console.warn("Failed to cache audio in IndexedDB:", e);
      }
      localPreview = URL.createObjectURL(fileToUpload);
    }

    const tempMessage: any = {
      isTempMsg: true,
      id: mediaTempId,
      message: content || "",
      timestamp: formatTimestamp(new Date()),
      isUser: true,
      replyMessage: replyingTo ? replyingTo.message : null,
      replyToId: replyingTo ? String(replyingTo.id) : null,
      replyMediaType: replyingTo?.mediaType || null,
      replyFilePath: replyingTo?.filePath || null,
      senderId,
      senderName,
      mediaType,
      filePath: localPreview || null,
      createdAt: new Date().toISOString(),
      uploadTaskId: taskId,
      uploadStatus: "uploading",
      uploadProgress: 0,
    };

    scrollToBottom(true); // capture position before DOM grows, clear divider immediately
    dispatch(addNewMessage(tempMessage));
    setMessage("");
    isSendingRef.current = false;

    // Subscribe to progress updates from uploadManager
    const unsub = uploadManager.subscribe(taskId, (t) => {
      dispatch(
        updateMessageUpload({
          id: mediaTempId,
          uploadProgress: t.progress,
          uploadStatus: t.status,
          error: t.error,
        }),
      );
    });

    try {
      const response: any = await uploadManager.upload(
        taskId,
        currentChat.id,
        formData,
      );

      unsub();

      // Upload was cancelled by the user — task state already reflects "failed".
      if (response?.cancelled) {
        audioCacheDB.updateStatus(taskId, "failed").catch(() => {});
        return;
      }

      const newMsg = formatMessage(response?.newMessage, userId);

      if (FEATURE_FLAGS.USE_CRDT_ENGINE && currentChat?.id && newMsg.id) {
        messageGraph.mirror(currentChat.id, newMsg);
      }
      dispatch(replaceMessageById({ id: mediaTempId, msg: newMsg }));

      // Cache sent media message in IndexedDB
      if (userId && currentChat?.id && newMsg.id) {
        messageCacheDB
          .putMessages(userId, currentChat.id, [newMsg])
          .catch(() => {});
      }

      if (currentChat?.isNewChat) {
        const updatedConvo = formatSingleChat(response?.conversation);
        dispatch(updateConversationList(updatedConvo));
      } else {
        dispatch(
          updateConversationList({
            id: currentChat?.id,
            mediaType: newMsg.mediaType,
            message: newMsg.lastMessage,
            time: newMsg.time,
            date: newMsg.date,
            lastUpdateToSort: newMsg.createdAt,
            ofUser: userId,
          }),
        );
      }

      dispatch(
        updateCurrentChat({
          mediaType: newMsg.mediaType,
          message: newMsg.lastMessage,
        }),
      );

      dispatch(moveChatToTop({ id: currentChat?.id || "" }));

      // Upload succeeded — remove cached blob from IndexedDB
      audioCacheDB.remove(taskId).catch(() => {});

      setScheduledDate(null);
    } catch (error: any) {
      unsub();
      // Mark as failed in IndexedDB (blob stays for retry)
      audioCacheDB.updateStatus(taskId, "failed").catch(() => {});
      if (!error?.message?.includes("cancelled")) {
        console.error("Error sending media:", error);
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            "Failed to send media message",
        );
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const sendPollMessage = async (
    question: string,
    options: any[],
    multiAllow: boolean,
  ) => {
    const data = {
      content: question,
      chatId: currentChat?.id,
      isGroup: true,
      isPoll: true,
      options,
      multiAllow,
    };

    try {
      const response: any = await triggerSendMessage(data).unwrap();

      const newMsg = formatMessage(
        response?.newMessage || response?.conversation,
        userId,
      );
      if (FEATURE_FLAGS.USE_CRDT_ENGINE && currentChat?.id && newMsg.id) {
        messageGraph.mirror(currentChat.id, newMsg);
      }
      scrollToBottom(true);
      dispatch(addNewMessage(newMsg));

      dispatch(
        updateConversationList({
          id: currentChat?.id,
          mediaType: newMsg.mediaType,
          message: newMsg.lastMessage,
          time: newMsg.time,
          date: newMsg.date,
          lastUpdateToSort: newMsg.createdAt,
          ofUser: userId,
        }),
      );

      dispatch(
        updateCurrentChat({
          mediaType: newMsg?.mediaType,
          message: newMsg?.lastMessage,
        }),
      );

      dispatch(moveChatToTop({ id: currentChat?.id || "" }));

      setPollCreatorModalOpen(false);
    } catch (error: any) {
      console.error("Error sending media:", error);
      toast.error(
        error.data?.message || error.message || "Failed to send poll message",
      );
      dispatch(removeLastMsg());
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendMessage = () => {
    if (isSendingRef.current || !currentChat) return;

    const hasText = message.trim();
    const hasMedia = selectedFile && mediaType;

    if (!hasText && !hasMedia) return;
    isSendingRef.current = true;
    setSendingMessage(true);

    if (hasMedia) {
      const mediaTask = {
        content: message,
        mediaType,
        file: selectedFile,
        isGroup: currentChat?.isGroup || false,
        isBroadCast: currentChat?.isBroadCast || false,
        replyTo: replyingTo ? replyingTo : null,
        date: scheduledDate || null,
      };

      sendMediaMessage(mediaTask);
      return;
    }

    const replyId = replyingTo?.id ? String(replyingTo.id) : undefined;
    const replyMsg = replyingTo?.message ? replyingTo.message : null;

    const getPhoneNumber = () => {
      if (currentChat.isGroup) {
        const currentUser = currentChat?.groupMembers?.find(
          (member: any) => member._id === userId,
        );
        return currentUser?.phoneNumber;
      } else {
        return storageService.getPhoneNumber() || "";
      }
    };
    const msg = {
      content: message,
      phoneNumber: getPhoneNumber(),
      iseFone: currentChat.iseFone,
      messageId: replyId,
      replyMsg,
      chatId: currentChat.id,
      isGroup: currentChat.isGroup || false,
      isBroadCast: currentChat.isBroadCast || false,
      replyMediaType: replyingTo?.mediaType || null,
      replyFilePath: replyingTo?.filePath || null,
      date: scheduledDate || null,
    };

    sendMessage(msg);
  };

  const handleFileChange: UploadProps["beforeUpload"] = (file: File) => {
    // Validate file size
    const { limit, label, category } = UPLOAD.getLimit(file.type);
    if (file.size > limit) {
      toast.error(
        `1 ${category} file you tried adding is larger then the ${label} limit.`,
      );
      return false;
    }

    setMessage("");
    setAudioURL(null);
    setVoiceVisible(false);

    const fileType = file.type;
    setSelectedFile(file);
    setInputVisible(false);
    let type: number = 3;
    if (fileType.startsWith("image/")) {
      type = 1;
    } else if (fileType.startsWith("video/")) {
      type = 2;
    } else if (fileType.startsWith("audio/")) {
      type = 4;
    } else if (fileType.startsWith("text/")) {
      type = 0;
    }

    setMediaType(type);
    setFileDetails({
      name: file.name,
      size: (file.size / 1024).toFixed(2) + " KB",
      type: fileType,
    });

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    return false;
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "doc",
      label: (
        <Upload
          accept="audio/*,.pdf,.doc,.docx,.xls,.xlsx,video/*"
          beforeUpload={handleFileChange}
          showUploadList={false}
          multiple={false}
        >
          <Flex gap={4} className="option-wrapper">
            <div className="dropdown-icon">
              <ClipIcon />
            </div>
            <span className="item-title">Document</span>
          </Flex>
        </Upload>
      ),
    },
    { type: "divider" },
    {
      key: "media",
      label: (
        <Upload
          accept="image/*,"
          beforeUpload={handleFileChange}
          showUploadList={false}
          multiple={false}
        >
          <Flex gap={4} className="option-wrapper">
            <div className="dropdown-icon">
              <PhotosIcon />
            </div>
            <span className="item-title">Gallery</span>
          </Flex>
        </Upload>
      ),
    },
    { type: "divider" },
    {
      key: "poll",
      label: (
        <Flex
          gap={4}
          className="option-wrapper"
          onClick={() => setPollCreatorModalOpen(true)}
        >
          <div className="dropdown-icon">
            <PollIcon />
          </div>
          <span className="item-title">Poll</span>
        </Flex>
      ),
    },
  ];

  // Cleanup stale audio cache entries on mount
  useEffect(() => {
    audioCacheDB.cleanup().catch(() => {});
  }, []);

  useEffect(() => {
    if (replyingTo) {
      if (replyingTo && textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }
  }, [replyingTo]);

  useEffect(() => {
    // this handles input and mediaType when audioUrl is changed from the CustomAudioRecorder component
    if (audioURL) {
      setInputVisible(false);
      setMediaType(4);
    } else {
      setInputVisible(true);
      setMediaType(null);
    }
  }, [audioURL]);

  useEffect(() => {
    const settings = currentChat?.groupSettings;
    const blocked = currentChat?.isBlocked;
    const isForwardMsg = currentChat?.isForwardMsg;
    const currentUser = currentChat?.groupMembers?.find(
      (member: any) => member._id === userId,
    );
    const isGroup = currentChat?.isGroup;

    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }

    if (!isGroup) {
      setCanSendMessage(true);
    }

    if (blocked) {
      setIsBlocked(true);
    } else {
      setIsBlocked(false);
    }

    if (isGroup && currentUser?.isAdmin) {
      setCanSendMessage(true);
    } else if (isGroup && settings) {
      setCanSendMessage(settings.sendMessage);
    }

    if (isGroup && !currentUser) {
      setIsNotGroupMember(true);
    } else {
      setIsNotGroupMember(false);
    }

    if (isForwardMsg && msgToForward) {
      if (isGroup && !currentUser) {
        toast.error("You are not a member anymore");
        return;
      }

      if (isGroup && !currentUser?.isAdmin && !settings.sendMessage) {
        toast.error("Only admins can send message");
        return;
      }

      if (currentChat?.isBlocked) {
        toast.error("Unblock to send message");
        return;
      }

      if (msgToForward?.mediaType === 0) {
        const msg = {
          content: msgToForward?.message,
          phoneNumber: currentChat?.sourceNumber,
          iseFone: currentChat.iseFone,
          messageId: undefined,
          replyMsg: null,
          chatId: currentChat.id,
          isGroup: currentChat.isGroup || false,
          isBroadCast: currentChat.isBroadCast || false,
        };

        sendMessage(msg);
      } else {
        const msg = {
          content: msgToForward?.message,
          mediaType: msgToForward.mediaType,
          mediaPath: msgToForward.filePath,
          isGroup: currentChat.isGroup || false,
          isBroadCast: currentChat.isBroadCast || false,
        };

        sendMediaMessage(msg);
      }

      const { isForwardMsg, ...rest } = currentChat;
      dispatch(setCurrentChat(rest));
      dispatch(setMsgToForward(null));
    }
  }, [currentChat, msgToForward]);

  useEffect(() => {
    setMessage("");
  }, [currentChat?.id]);

  return (
    <>
      <div
        className="chat-footer"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();

            if (message.trim() || selectedFile || audioURL) {
              handleSendMessage();
            }
          }
        }}
      >
        {inputVisible && (
          <TextArea
            disabled={
              !canSendMessage || isBlocked || isNotGroupMember || recording
            }
            autoSize={{
              maxRows: 5,
            }}
            ref={textAreaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              !canSendMessage
                ? "Only admins can send message"
                : isBlocked
                  ? "Unblock to send message"
                  : isNotGroupMember
                    ? "You are not a member anymore"
                    : "Type a message..."
            }
            className="input-textarea"
          />
        )}

        {audioURL && (
          <Flex align="center" gap={8}>
            <AudioPlayer audioUrl={audioURL} />
            <DeleteOutlined
              onClick={deleteAttachment}
              style={{ color: "red", marginBottom: 4 }}
            />
          </Flex>
        )}

        {previewUrl && (
          <Flex vertical gap={10}>
            {mediaType === 1 && (
              <Flex align="center" gap={8}>
                <Image
                  src={previewUrl}
                  alt="Uploaded file"
                  style={{
                    maxWidth: "200px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
                <DeleteOutlined
                  onClick={deleteAttachment}
                  style={{ color: "red" }}
                />
              </Flex>
            )}

            {mediaType === 2 && previewUrl && (
              <Flex align="center" gap={8}>
                <video
                  src={previewUrl}
                  controls
                  style={{
                    maxWidth: "200px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    display: "block",
                  }}
                />
                <DeleteOutlined
                  onClick={deleteAttachment}
                  style={{ color: "red" }}
                />
              </Flex>
            )}

            {mediaType === 3 && selectedFile && (
              <Flex align="center" gap={8}>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="selected-file"
                >
                  <FileOutlined className="file-icon" />
                  <Text className="file-name">{fileDetails?.name || null}</Text>
                </a>
                <DeleteOutlined
                  onClick={deleteAttachment}
                  style={{ color: "red" }}
                />
              </Flex>
            )}

            {mediaType === 4 && (
              <Flex align="center" gap={8}>
                <AudioPlayer audioUrl={previewUrl} />
                <DeleteOutlined
                  onClick={deleteAttachment}
                  style={{ color: "red" }}
                />
              </Flex>
            )}
          </Flex>
        )}

        {canSendMessage && !isBlocked && !isNotGroupMember && (
          <Flex className="controls" justify="space-between" align="center">
            <Flex align="center" gap={8}>
              {schedulerVisible && (
                <Popover
                  content={
                    <DateTimePicker
                      onClose={() => setDateTimePickerOpen(false)}
                      returnDateTime={setScheduledDate}
                    />
                  }
                  trigger="click"
                  open={dateTimePickerOpen}
                  onOpenChange={setDateTimePickerOpen}
                  placement="topLeft"
                >
                  <Button
                    className="open-btn"
                    type="text"
                    disabled={!message && !selectedFile}
                  >
                    <img
                      src={`/assets/icons/${theme}/subscription.png`}
                      width={20}
                      height={20}
                      alt="subscription"
                    />
                  </Button>
                </Popover>
              )}

              <Popover
                content={
                  <Picker
                    data={data}
                    onSelect={onEmojiSelect}
                    theme={theme}
                    showPreview={false}
                    showSkinTones={false}
                  />
                }
                trigger="click"
                open={emojiPickerOpen}
                onOpenChange={setEmojiPickerOpen}
                placement="topLeft"
                overlayClassName="emoji-picker-popover"
              >
                <Button
                  type="text"
                  icon={<SmileOutlined />}
                  className="emoji-trigger-btn"
                  disabled={
                    !canSendMessage ||
                    isBlocked ||
                    isNotGroupMember ||
                    recording
                  }
                />
              </Popover>

              {uploadVisible && (
                <Dropdown
                  overlayClassName="custom-dropdown upload-options-dropdown"
                  menu={{ items: menuItems }}
                  trigger={["click"]}
                >
                  <Button
                    onClick={(e) => e.stopPropagation()}
                    className="upload-btn"
                    type="text"
                    icon={<PlusOutlined />}
                  />
                </Dropdown>
              )}

              {voiceVisible && (
                <CustomAudioRecorder
                  setAudio={setAudioURL}
                  setRecording={setRecording}
                  setAudioBlob={setSelectedFile}
                />
              )}
            </Flex>

            <Flex align="center" gap={8}>
              <Button
                className="send-btn"
                type="primary"
                onClick={() => {
                  handleSendMessage();
                }}
              >
                {scheduledDate ? "Schedule" : "Send"}
              </Button>
              {scheduledDate && (
                <Button
                  className="cancel-btn"
                  type="primary"
                  onClick={() => setScheduledDate(null)}
                >
                  Cancel
                </Button>
              )}
            </Flex>
          </Flex>
        )}
      </div>
      <Modal
        className="custom-modal"
        centered
        open={pollCreatorModalOpen}
        footer={null}
        onCancel={() => setPollCreatorModalOpen(false)}
      >
        <PollCreator onSend={sendPollMessage} />
      </Modal>
    </>
  );
};

export default ChatFooter;
